import { useEffect, useMemo } from 'react';
import { useCurrentEnterprise } from '@mews-commander/legacy-entities/src/enterprise';
import { Button, Card, Container, Stack } from '@optimus-web/core';
import { LocalizedText, lt, showErrorAlert, showSuccessAlert, useFormControls } from '@mews/framework';
import { TextKey, RouteName } from '@mews-commander/enums';
import { createOption, LocalizedTypography, ModalWrapper, OverlayedLoader } from '@mews-commander/ui';
import { useResolveRoute } from '@mews/routing';
import { SelectField } from '@mews-commander/form';
import { useRepositoryMutation } from '@mews-commander/data-access';
import { getServiceIndexUrl } from '@mews-commander/legacy-entities/src/selectors/services';
import { useCommanderSelector } from '@mews-commander/utils';
import ModalHeader from '../../../legacy/React/Components/Common/ModalHeader';
import { Breadcrumbs } from '../../../components/BreadcrumbList';
import { AccountingCategoriesField, AccountingCategoriesFormValues } from '../constants';
import { useAccountingCategories } from '../../enterpriseRateManagement/components/sections/components/rateForm/fields/accountingCategoriesKey/useAccountingCategories';
import { useGetService } from '../utils/useGetService';

interface ProductServiceAccountingCategoriesPageProps {
    enterpriseId: string;
    productServiceId: string;
    additionalServicesName: string | undefined;
}

export const ProductServiceAccountingCategoriesPage = ({
    enterpriseId,
    productServiceId,
    additionalServicesName,
}: ProductServiceAccountingCategoriesPageProps) => {
    const { data: service, isLoading: isLoadingServices } = useGetService([enterpriseId], productServiceId);

    const categories = useMemo(
        () => ({
            accountingCategoryId: service?.accountingCategoryId,
            rebateAccountingCategoryId: service?.rebateAccountingCategoryId,
            canceledItemAccountingCategoryId: service?.canceledItemAccountingCategoryId,
        }),
        [service?.accountingCategoryId, service?.rebateAccountingCategoryId, service?.canceledItemAccountingCategoryId]
    );

    const { data: { accountingCategories = null } = {}, isLoading: isLoadingAccountingCategories } =
        useAccountingCategories([enterpriseId]);

    const { mutateAsync: mutateAccountingCategories } = useRepositoryMutation(
        'services',
        'updateProductServiceAccountingCategories',
        {
            onSuccess: () => {
                showSuccessAlert({ description: lt('SaveSuccessMessage') });
            },
            onError: () => {
                showErrorAlert({ description: lt('UnexpectedErrorMessage') });
            },
            invalidateQueries: { services: ['getAll'] },
        }
    );

    const resolveRoute = useResolveRoute();
    const enterprise = useCurrentEnterprise();
    const enterpriseUrl = resolveRoute(RouteName.EnterpriseDetail, {
        enterpriseId,
    });

    const serviceIndexBreadcrumbUrl = useCommanderSelector((state) =>
        getServiceIndexUrl(state, { enterpriseId, resolveRoute })
    );

    const additionalServiceUrl = resolveRoute(RouteName.ProductServiceDetail, {
        id: productServiceId,
    });

    const breadCrumbs: Breadcrumbs = [
        {
            text: enterprise.name,
            url: enterpriseUrl,
        },
        {
            text: <LocalizedText textKey={TextKey.ServicePlural} />,
            url: serviceIndexBreadcrumbUrl,
        },
        {
            url: additionalServiceUrl,
            text: additionalServicesName ?? '',
        },
        {
            active: true,
            text: <LocalizedText textKey={TextKey.Accounting} />,
        },
    ];

    const onSubmit = async (values: AccountingCategoriesFormValues) => {
        const newCategories = {
            ...categories,
            ...Object.fromEntries(Object.entries(values).filter(([, value]) => value !== null && value !== undefined)),
        };

        await mutateAccountingCategories({
            ...newCategories,
            serviceId: productServiceId,
        });
    };

    const { renderFormContent, isSubmitting, reset } = useFormControls<AccountingCategoriesFormValues>({
        onSubmit,
        mode: 'onChange',
        defaultValues: categories,
    });

    useEffect(() => {
        if (categories) reset(categories, { keepDirtyValues: true });
    }, [categories, reset]);

    const isLoading = isLoadingServices || isLoadingAccountingCategories || isSubmitting;

    const options =
        accountingCategories?.map((category) =>
            createOption({
                label: category.name,
                value: category.id,
            })
        ) || [];

    return (
        <ModalWrapper>
            <ModalHeader breadcrumbs={breadCrumbs} />
            <Stack>
                <Container align="center" padding={['400', '0']} minWidth="360px">
                    <Card padding="400">
                        <Stack vertical spacing="400">
                            <OverlayedLoader fixed show={isLoading} />
                            <LocalizedTypography textKey={TextKey.Accounting} textStyle="bodyLargeStrong" as="div" />
                            {renderFormContent(
                                <Stack vertical spacing="200">
                                    <SelectField
                                        options={options}
                                        name={AccountingCategoriesField.AccountingCategory}
                                        label={<LocalizedText textKey={TextKey.AccountingCategory} />}
                                    />
                                    <SelectField
                                        options={options}
                                        name={AccountingCategoriesField.RebateAccountingCategory}
                                        label={<LocalizedText textKey={TextKey.RebateAccountingCategory} />}
                                    />
                                    <SelectField
                                        options={options}
                                        name={AccountingCategoriesField.CanceledItemAccountingCategory}
                                        label={<LocalizedText textKey={TextKey.CanceledItemAccountingCategory} />}
                                    />

                                    <Stack alignment="end">
                                        <Button type="submit" variant="primary">
                                            <LocalizedText textKey={TextKey.Save} />
                                        </Button>
                                    </Stack>
                                </Stack>
                            )}
                        </Stack>
                    </Card>
                </Container>
            </Stack>
        </ModalWrapper>
    );
};
